// Multi-family: Guest passes CRUD page (P1-10).
//
// Also covers Stitch v2 design `virtual_keys_axn_pro_access_branding`
// via the global useBranding() hook: the page header logo + accent
// color follow the operator's saved branding when set, otherwise fall
// back to the default chrome.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Button, Icon } from '@ng-unifi/ui';
import { CreateGuestPassBody } from '@ng-unifi/domain';
import { apiClient, ApiError } from '../lib/api.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { useBranding } from '../lib/branding.js';
import { CrudTable, fieldCls } from '../components/CrudTable.js';
import { CrudModal, FormField } from '../components/CrudModal.js';
import { RowActions, Toast } from '../components/RowActions.js';
import { LocalOnlyBanner } from '../components/LocalOnlyBanner.js';

interface GuestPassRow {
  id: string;
  sponsor_profile_id: string;
  guest_name: string;
  guest_email: string | null;
  valid_from: string;
  valid_until: string;
  credential_id: string | null;
}

interface ResidentLite {
  id: string;
  name: string;
  household_id: string;
}

async function loadGuestPasses(): Promise<GuestPassRow[]> {
  const res = await apiClient.get<{ data: GuestPassRow[] }>('/api/v1/guest-passes');
  return res.data;
}

async function loadResidents(): Promise<ResidentLite[]> {
  const res = await apiClient.get<{ data: ResidentLite[] }>('/api/v1/residents');
  return res.data;
}

type ModalKind = 'create' | 'revoke';
type ModalState = { kind: ModalKind; row: GuestPassRow | null };

export function GuestPassesPage() {
  const { branding } = useBranding();
  const [rows, setRows] = useState<GuestPassRow[] | null>(null);
  const [residents, setResidents] = useState<ResidentLite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    Promise.all([loadGuestPasses(), loadResidents()])
      .then(([gs, rs]) => {
        if (cancelled) return;
        setRows(gs);
        setResidents(rs);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  const sponsorName = (id: string) =>
    residents.find((r) => r.id === id)?.name ?? id;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          {branding.logo_url ? (
            <img
              src={branding.logo_url}
              alt=""
              className="h-12 w-12 object-contain rounded-lg border border-outline-variant"
            />
          ) : (
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary"
              style={
                branding.brand_color
                  ? { background: branding.brand_color, color: '#fff' }
                  : undefined
              }
            >
              <Icon name="card_membership" className="text-2xl" />
            </div>
          )}
          <div>
            <h1
              className="font-headline-md text-headline-md text-on-surface"
              style={branding.brand_color ? { color: branding.brand_color } : undefined}
            >
              Guest passes
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Sponsored temporary records. No controller credential issued yet.
            </p>
          </div>
        </div>
        <Button
          onClick={() => setModal({ kind: 'create', row: null })}
          disabled={residents.length === 0}
          style={branding.brand_color ? { background: branding.brand_color } : undefined}
        >
          <Icon name="add" />
          <span>Issue pass</span>
        </Button>
      </header>

      <LocalOnlyBanner
        title="Local records only"
        body="Guest passes are stored locally for now and are NOT pushed to the UniFi controller. They do not unlock any doors yet — controller credential hand-off lands in a follow-up tranche."
      />

      {toast ? <Toast message={toast} onDismiss={() => setToast(null)} /> : null}

      <CrudTable<GuestPassRow>
        rows={rows}
        error={error}
        emptyMessage={
          residents.length === 0
            ? 'Add a resident first — they sponsor each guest pass.'
            : 'No guest passes issued.'
        }
        columns={[
          { key: 'guest_name', header: 'Guest', width: '20%' },
          {
            key: 'guest_email',
            header: 'Email',
            render: (g) =>
              g.guest_email ?? <span className="text-on-surface-variant">—</span>,
          },
          {
            key: 'sponsor_profile_id',
            header: 'Sponsor',
            render: (g) => sponsorName(g.sponsor_profile_id),
          },
          {
            key: 'valid_from',
            header: 'Valid from',
            width: '14%',
            render: (g) => new Date(g.valid_from).toLocaleString(),
          },
          {
            key: 'valid_until',
            header: 'Until',
            width: '14%',
            render: (g) => new Date(g.valid_until).toLocaleString(),
          },
          {
            key: 'credential_id',
            header: 'Credential',
            width: '12%',
            render: (g) =>
              g.credential_id ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase bg-secondary-container text-on-secondary-container">
                  Linked
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full font-label-md text-label-md uppercase bg-surface-container-high text-on-surface-variant">
                  Not issued
                </span>
              ),
          },
        ]}
        actions={(g) => (
          <RowActions
            actions={[
              {
                label: 'Revoke',
                destructive: true,
                onClick: () => setModal({ kind: 'revoke', row: g }),
              },
            ]}
          />
        )}
      />

      {modal ? (
        <GuestPassModal
          state={modal}
          residents={residents}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function defaultRange(): { from: string; until: string } {
  const now = new Date();
  const start = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  const end = new Date(start.getTime() + 24 * 60 * 60_000);
  // datetime-local needs YYYY-MM-DDTHH:MM (no timezone, no seconds).
  return { from: start.toISOString().slice(0, 16), until: end.toISOString().slice(0, 16) };
}

function GuestPassModal({
  state,
  residents,
  onCancel,
  onDone,
}: {
  state: ModalState;
  residents: ResidentLite[];
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { kind, row } = state;
  const { key: idemKey } = useIdempotencyKey();
  const defaults = defaultRange();
  const [sponsorId, setSponsorId] = useState(residents[0]?.id ?? '');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [validFrom, setValidFrom] = useState(defaults.from);
  const [validUntil, setValidUntil] = useState(defaults.until);
  const [recurring, setRecurring] = useState(false);
  const [restrictions, setRestrictions] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (kind === 'create') {
        const body = CreateGuestPassBody.parse({
          sponsor_profile_id: sponsorId,
          guest_name: guestName.trim(),
          guest_email: guestEmail.trim() || null,
          valid_from: new Date(validFrom).toISOString(),
          valid_until: new Date(validUntil).toISOString(),
          recurring_rule: recurring ? { enabled: true } : null,
          restrictions:
            restrictions.trim().length > 0
              ? { note: restrictions.trim() }
              : null,
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .post('/api/v1/guest-passes', body);
        onDone(`Guest pass issued for ${body.guest_name}.`);
        return;
      }
      if (kind === 'revoke') {
        await apiClient.delete(`/api/v1/guest-passes/${row!.id}`);
        onDone(`Guest pass for ${row!.guest_name} revoked.`);
        return;
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed');
      setSubmitting(false);
    }
  }

  if (kind === 'revoke') {
    return (
      <CrudModal
        title={`Revoke pass for ${row?.guest_name}`}
        icon="block"
        tone="destructive"
        submitting={submitting}
        error={error}
        submitLabel="Revoke"
        onCancel={onCancel}
        onSubmit={onSubmit}
      >
        <p className="font-body-md text-body-md text-on-surface">
          Revoking deletes the local guest pass. When controller credential
          hand-off lands, this will also yank any issued credential.
        </p>
      </CrudModal>
    );
  }

  return (
    <CrudModal
      title="Issue guest pass"
      icon="card_membership"
      submitting={submitting}
      error={error}
      submitLabel="Issue"
      canSubmit={
        sponsorId.length > 0 &&
        guestName.trim().length > 0 &&
        validFrom.length > 0 &&
        validUntil.length > 0 &&
        new Date(validUntil) > new Date(validFrom)
      }
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      <FormField id="gp-sponsor" label="Sponsor (resident)">
        <select
          id="gp-sponsor"
          required
          value={sponsorId}
          onChange={(e) => setSponsorId(e.target.value)}
          className={fieldCls}
        >
          {residents.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </FormField>
      <FormField id="gp-name" label="Guest name">
        <input
          id="gp-name"
          required
          maxLength={120}
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          className={fieldCls}
        />
      </FormField>
      <FormField id="gp-email" label="Guest email" hint="Optional.">
        <input
          id="gp-email"
          type="email"
          maxLength={254}
          value={guestEmail}
          onChange={(e) => setGuestEmail(e.target.value)}
          className={fieldCls}
        />
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField id="gp-from" label="Valid from">
          <input
            id="gp-from"
            type="datetime-local"
            required
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            className={fieldCls}
          />
        </FormField>
        <FormField id="gp-until" label="Valid until">
          <input
            id="gp-until"
            type="datetime-local"
            required
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            className={fieldCls}
          />
        </FormField>
      </div>
      <fieldset
        className="border border-outline-variant rounded-lg p-3 space-y-2"
      >
        <legend className="font-label-md text-label-md text-on-surface-variant px-1 uppercase">
          Advanced options
        </legend>
        <label className="inline-flex items-center gap-2 font-body-md text-body-md">
          <input
            type="checkbox"
            checked={recurring}
            onChange={(e) => setRecurring(e.target.checked)}
            aria-label="Recurring"
          />
          <span>Recurring access rule</span>
        </label>
        <FormField id="gp-restrictions" label="Door restrictions">
          <input
            id="gp-restrictions"
            value={restrictions}
            onChange={(e) => setRestrictions(e.target.value)}
            placeholder="Optional note, e.g. Lobby only"
            className={fieldCls}
          />
        </FormField>
        <p className="font-body-md text-body-md text-on-surface-variant">
          These fields are stored as optional metadata and can be expanded into
          stricter controller rules in a follow-up tranche.
        </p>
      </fieldset>
    </CrudModal>
  );
}
